// The quality line under a Prakrit lesson or story: "Checked by a teacher", the reviewer's name and the date of the last
// review. Renders nothing without a credit (never "awaiting review" for learners). The date is built by hand in India
// time, so the server and the browser always write the same text.

import type { FC, ReactNode } from 'react'
import type { ReviewCreditData } from '@/prakrit/lib/server/credits'

import { BadgeCheck } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ReviewCreditProps {
    credit: ReviewCreditData | null | undefined
    className?: string
}

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2026-10-23T20:15:00Z" → "24 Oct 2026" (Asia/Kolkata, UTC+5:30, no daylight saving). */
const indiaDate = (iso: string): string | null => {
    const time = Date.parse(iso)

    if (Number.isNaN(time)) return null

    const d = new Date(time + 330 * 60 * 1000)

    return `${d.getUTCDate()} ${MONTHS_EN[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

const ReviewCredit: FC<ReviewCreditProps> = ({ credit, className }): ReactNode => {
    if (!credit) return null

    const date = indiaDate(credit.reviewedAt)

    return (
        <p className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground', className)}>
            <BadgeCheck className='size-4 shrink-0 text-success' aria-hidden='true' />
            <span lang='hi' className='hindi'>शिक्षक द्वारा जाँचा गया:</span>
            <span className='font-medium text-foreground'>{credit.name}</span>
            {date && (
                <>
                    <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
                    <time dateTime={credit.reviewedAt} className='tabular-nums'>{date}</time>
                </>
            )}
            <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
            <span lang='en'>Checked by a teacher</span>
        </p>
    )
}

export default ReviewCredit
